<template>
  <stihl-modal id="sync-modal" @modal-closed="$emit('modalClosed')" v-if="user">

    <div class="title" id="currentSyncState">
      <h3>{{ $t('adm.page.sync.title') }}</h3>
      <sync-badge :flag="user.syncFlag"/>
    </div>

    <div id="userBlock" class="modal-body" v-if="user">
      <user-table :user="user"/>
    </div>

    <div id="matchesBlock" class="modal-body" v-if="user">
      <stihl-flag :message="$t('adm.page.sync.contacts')"/>
      <contacts-table
          ref="contactsTable"
          :init-selected-contact="user?.crmId"
          :user="user"
          @contact-selected="match = $event"
          @organization-clicked="$emit('organizationClicked', $event)"/>
    </div>

    <div class="modal-footer buttons">
      <div class="btn-group" role="group">
        <button
            id="selectContact"
            type="button"
            class="btn btn-primary"
            @click="save('match')"
            :disabled="!match || match?.inactive">
          {{ $t('adm.page.sync.btnSelect') }}
        </button>
        <button id="createNewContact" type="button" class="btn btn-default" @click="save('new')" :disabled="user.syncFlag && !user.crmId">
          {{ $t('adm.page.sync.btnCreate') }}
        </button>
        <button id="blockSync" type="button" class="btn btn-default" @click="save('block')" :disabled="false === user.syncFlag">
          {{ $t('adm.page.sync.btnBlock') }}
        </button>
      </div>
      <button name="cancel" type="button" class="btn link-with-icon" data-bs-dismiss="modal" :aria-label="$t('adm.button.cancel')">
        <img src="@/assets/img/icons/x.svg" :alt="$t('adm.button.cancel')">
        {{ $t('adm.button.cancel') }}
      </button>
    </div>

  </stihl-modal>

</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Backend, { Change, CrmContact, UserAttr, UserData } from '@/components/backend';
import StihlModal from '@/components/ui/StihlModal.vue';
import StihlFlag from '@/components/ui/StihlFlag.vue';
import SyncBadge from '@/components/ui/SyncBadge.vue';
import { Logger } from 'oidc-client-ts';
import { mapWritableState } from 'pinia';
import useAppStore from '@/components/store';
import ContactsTable from './ContactsTable.vue';
import UserTable from './UserTable.vue';
import { arraySize, equalSets, intersectionSet, unionSet } from '@/components/utils';

export default defineComponent({
  name: 'SynchronizationModal',
  components: {
    ContactsTable, SyncBadge, StihlFlag, StihlModal, UserTable,
  },
  emits: ['modalClosed', 'organizationClicked', 'syncUpdated'],

  data: () => ({
    match: undefined as CrmContact | undefined,
    state: 'INI' as 'INI' | 'RDY' | 'ERR',
  }),

  computed: {
    ...mapWritableState(useAppStore, {
      user: 'showUserSyncModal', // modal-control -> set UserData to show modal
    }),
  },

  methods: {

    async save(opt: 'match' | 'new' | 'block') {
      const syncFlag = opt !== 'block';
      const contact = this.match;

      // user is defined if this is called
      const userData = this.user as UserData;

      const changes: Change<UserAttr>[] = [
        { op: 'replace', field: 'syncFlag', value: String(syncFlag) }, // always update the flag
      ];

      // get a valid token
      const api = await Backend.validateToken();

      if (opt === 'match') {
        if (!contact) throw new Error('contact match must be defined');

        if (contact.crmId) {
          changes.push({ op: 'replace', field: 'crmId', value: contact.crmId }); // update crm-id
        }

        // role not visible - if not configured or no roles available
        if (!window.APP_CONFIG?.roles?.show) {
          const countryActiveRoles = !this.user?.organization ? [] : (await api.getActiveRoles(this.user.organization)).data;
          //Logger.info(`[sync] countryActiveRoles: <${countryActiveRoles}> userExistingRoles: <${this.user?.roles}> contactRoles: <${contact.roles}>`);
          if (arraySize(countryActiveRoles) > 0) {
            // filter user roles because the backend is only allowing the roles from country
            const newRoles = intersectionSet(unionSet(this.user?.roles, contact.roles), countryActiveRoles);
            //Logger.info(`[sync] newRoles: <${newRoles}>`);
            if (newRoles && newRoles.length > 0 && !equalSets(newRoles, this.user?.roles as string[])) {
              changes.push({ op: 'replace', field: 'roles', value: newRoles });
            }
          }
        }

      } else if (userData.crmId) {
        changes.push({ op: 'remove', field: 'crmId' });
      }

      await api.updateUser(userData.id, changes);
      Logger.info(`[sync] saved flag:${syncFlag} crm-id:${contact?.crmId}`);

      // update the selected user in table
      userData.syncFlag = syncFlag;
      userData.crmId = opt === 'new' ? undefined : contact?.crmId;
      // inform user
      this.$emit('syncUpdated', opt);
      // close this modal
      this.$emit('modalClosed');
    },
  },
});
</script>

<style>
#sync-modal {
  --stihl-modal-content-gap: 1.875rem;
  --bs-modal-width: 88rem;
}

#sync-modal .title {
  display: flex;
  align-content: flex-start;
  justify-items: flex-start;
  gap: 1rem;
}

#sync-modal .title h3 {
  max-width: fit-content;

  color: black;
  font-feature-settings: 'clig' off, 'liga' off;

  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.5rem;

  margin: 0;
}

#sync-modal .buttons {
  display: flex;
  justify-content: space-between;
}
</style>
